import { Box, Tab, Tabs } from "@mui/material";
import type { DateTime } from "luxon";
import type React from "react";
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useHandleNurseChange } from "../../hooks/useHandleNurseChange";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { CONTROL_HEIGHT, INK, STAFF_PICKER, STATUS_BADGE } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { isShiftDayInPast } from "../../utils/dateUtils";
import type { Nurse, NurseFullRole } from "../../utils/models";
import { inverseRolesMap } from "../../utils/utilFunctions";
import { AddNurse } from "../AddNurse/AddNurse";
import { MoveNurse } from "../approvalDialogs/MoveNursePanel";
import { NurseRemovalContent } from "../approvalDialogs/NurseRemovalContent";
import { ErrorBoundary } from "../ErrorBoundary";
import { initialRecurringShiftDataStructure } from "../RecurringShift/RecurringShift";
import { RecurringShiftPanelWrapper } from "../RecurringShift/RecurringShiftPanelWrapper";
import { DaysInWeek } from "../RecurringShift/util/DaysInWeekSelector";
import { SwapStaff } from "../SwapStaff/SwapStaff";
import { CreateShiftContent } from "../staffingSearch/CreateShiftContent";
import { MultiSizePopup } from "../UIToolkit/Popups/MultiSizePopup/MultiSizePopup";
import { MultiSizePopupState } from "../UIToolkit/Popups/MultiSizePopup/utils";

interface StaffingChangesProps {
	setIsAdd: (value: boolean) => void;
	dayNo: number;
	shiftNo: number;
	// Per-cell data-week index. Required when the cell may be a visible-window spillover
	// (Tue-view's Sun/Mon, etc.) where the global WeekIndexProvider value points at the
	// previous data-week. Defaults to the global context for non-rotated callers.
	weekIndex?: number;
	rolesToDisplay: NurseFullRole[];
	onNurseSelected?: (nurse: Nurse, selectedDate: DateTime | null) => void;
	hideRoomSelect?: boolean;
	isCharged?: boolean;
	onClose?: (success?: boolean) => void;
	onShiftChange?: (shift: number) => void;
	showActual?: boolean;
	defaultTab?: TabType;
	onSelectedDateChange?: (date: DateTime) => void;
	lockedTimes?: { startTime: DateTime; endTime: DateTime };
	defaultRoom?: string;
	/** Pre-selects this job in the Post Shift (create-shift) tab and in Add Staff's shift step. */
	defaultJobId?: string;
	/**
	 * Add Staff opens with its default shift already picked (true, the default) or on "Pick a shift first"
	 * (false) — for entry points that open on a whole day rather than one shift.
	 */
	initialShiftSelected?: boolean;
	/**
	 * Hide the recommendations list and auto-check the prefilled manual row — i.e. this dialog was opened
	 * to post one specific, prefilled shift (e.g. a gap click). Defaults to `!!defaultRoom` for callers that
	 * don't set it. Pass explicitly so it doesn't break when the location is intentionally empty (dept-wide).
	 */
	hideRecommendations?: boolean;
}

export type TabType = "add-nurse" | "remove-nurse" | "swap-staff" | "create-shift" | "move-nurse" | "recurring-shift";

/** i18n key of each tab's label — also the minimized popup's title. */
const tabToLabelKey: Record<TabType, string> = {
	"add-nurse": "staffing.changes.tab.add.staff",
	"remove-nurse": "staffing.changes.tab.remove.staff",
	"swap-staff": "staffing.changes.tab.swap.staff",
	"create-shift": "staffing.changes.tab.post.shift",
	"move-nurse": "staffing.changes.tab.move.nurse",
	"recurring-shift": "staffing.changes.tab.recurring.shift",
};

const MaximizedStaffingChangesBody = ({
	setIsAdd,
	dayNo,
	shiftNo,
	rolesToDisplay,
	onNurseSelected,
	hideRoomSelect = false,
	isCharged = false,
	onClose,
	onShiftChange,
	showActual = false,
	onSelectedDateChange,
	setOpen,
	handleClose,
	isShiftInPast,
	activeTab,
	setActiveTab,
	setShiftNo,
	lockedTimes,
	defaultRoom,
	defaultJobId,
	hideRecommendations,
	initialShiftSelected,
	weekIndex: propWeekIndex,
}: Omit<StaffingChangesProps, "defaultTab"> & {
	setOpen: Dispatch<SetStateAction<boolean>>;
	handleClose: (success?: boolean) => void;
	activeTab: TabType;
	setActiveTab: Dispatch<SetStateAction<TabType>>;
	isShiftInPast: boolean;
	shiftNo: number;
	setShiftNo: Dispatch<SetStateAction<number>>;
}) => {
	const { t } = useTranslation();
	const { getShiftDate, isAutoScheduler, isHospitalDemo, isHospitalView, isSnfDemo } = useHospitalData();
	const { swapNurses } = useHandleNurseChange();
	const { weekIndex: ctxWeekIndex } = useWeekIndex();
	const weekIndex = propWeekIndex ?? ctxWeekIndex;

	useEffect(() => {
		onShiftChange?.(shiftNo);
	}, [shiftNo, onShiftChange]);

	useEffect(() => {
		if (activeTab === "add-nurse") {
			trackEvent("Staffing - open add nurse", {
				source: "add_nurse_dialog",
				week_index: weekIndex,
				day_no: dayNo,
				shift_no: shiftNo,
				date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
			});
		}
		if (activeTab === "create-shift") {
			trackEvent("Staffing - open create shift", {
				source: "add_nurse_dialog",
				week_index: weekIndex,
				day_no: dayNo,
				shift_no: shiftNo,
				date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
			});
		}
		if (activeTab === "remove-nurse") {
			trackEvent("Staffing - open remove nurse", {
				source: "staffing_changes_dialog",
				week_index: weekIndex,
				day_no: dayNo,
				shift_no: shiftNo,
				date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
			});
		}
		if (activeTab === "swap-staff") {
			trackEvent("Staffing - open swap staff", {
				source: "staffing_changes_dialog",
				week_index: weekIndex,
				day_no: dayNo,
				shift_no: shiftNo,
				date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
			});
		}
		if (activeTab === "recurring-shift") {
			trackEvent("Staffing - open recurring shift", {
				source: "staffing_changes_dialog",
				week_index: weekIndex,
				day_no: dayNo,
				shift_no: shiftNo,
				date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
			});
		}
	}, [activeTab, dayNo, getShiftDate, shiftNo, weekIndex]);

	const handleTabChange = (_: React.SyntheticEvent, newValue: TabType) => {
		setActiveTab(newValue);
	};

	const isRecurringShiftEnabled = useFeatureFlag(FEATURE_FLAGS.HasRecurringShift);
	return (
		<Box sx={{ width: "90vw", maxHeight: "90vh", overflow: "hidden" }}>
			<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					sx={{
						minHeight: CONTROL_HEIGHT.LARGE,
						"& .MuiTab-root": {
							minHeight: CONTROL_HEIGHT.LARGE,
							textTransform: "none",
							fontSize: "16px",
							fontWeight: "medium",
							color: STATUS_BADGE.BRAND_FALLBACK,
							"&.Mui-selected": {
								color: INK.SECONDARY,
								fontWeight: "bold",
							},
						},
					}}
				>
					<Tab label={t(tabToLabelKey["add-nurse"])} value="add-nurse" data-testid="staffing-tab-add-nurse" />
					<Tab label={t(tabToLabelKey["remove-nurse"])} value="remove-nurse" data-testid="staffing-tab-remove-nurse" />
					<Tab label={t(tabToLabelKey["swap-staff"])} value="swap-staff" />
					{!isAutoScheduler && (
						<Tab
							disabled={isShiftInPast}
							data-testid="staffing-tab-create-shift"
							data-annie-target="staffing-post-shift-tab"
							label={t(tabToLabelKey["create-shift"])}
							value="create-shift"
						/>
					)}
					{(isAutoScheduler || isSnfDemo || isHospitalView || isHospitalDemo) && <Tab label={t(tabToLabelKey["move-nurse"])} value="move-nurse" />}
					{isRecurringShiftEnabled && <Tab label={t(tabToLabelKey["recurring-shift"])} value="recurring-shift" />}
				</Tabs>
			</Box>

			{activeTab === "add-nurse" && (
				<ErrorBoundary>
					<AddNurse
						setOpen={setOpen}
						setIsAdd={setIsAdd}
						dayNo={dayNo}
						shiftNo={shiftNo}
						weekIndex={weekIndex}
						rolesToDisplay={rolesToDisplay}
						onNurseSelected={onNurseSelected}
						hideRoomSelect={hideRoomSelect}
						isCharged={isCharged}
						onClose={handleClose}
						onShiftChange={onShiftChange}
						showActual={showActual}
						lockedTimes={lockedTimes}
						defaultRoom={defaultRoom}
						defaultJobId={defaultJobId}
						initialShiftSelected={initialShiftSelected}
						// The hospital flow hands the one pick to `onNurseSelected` and lands it pending, so it takes
						// exactly one nurse. `onNurseSelected` alone doesn't: Add Staff only calls it under
						// `hideRoomSelect`, so the non-hospital Day Schedule (which always passes it) adds several.
						selectionMode={hideRoomSelect ? "single" : "multiple"}
						// Add Staff publishes to whatever day it is on, so its header may move days (AddNurse itself
						// keeps a request's locked shift, the hospital hand-off and worked hours on their own day).
						allowDateChange
						onSelectedDateChange={onSelectedDateChange}
					/>
				</ErrorBoundary>
			)}

			{activeTab === "remove-nurse" && (
				<ErrorBoundary>
					<NurseRemovalContent
						weekIndex={weekIndex}
						dayIndex={dayNo}
						onClose={handleClose}
						source="staffing_changes_dialog"
						showActual={showActual}
						// Every shift on the date is listed (grouped by window), so there is no single Day / Night slot to
						// report back through `onShiftChange`, and the list opens on "All shifts".
						selectionMode="multiple"
						initialWindow={null}
						// Like Add Staff, the removal lands on whatever day the tab is on (worked hours stay on their own day).
						allowDateChange
						onSelectedDateChange={onSelectedDateChange}
						// Content-sized, as the Remove tab always was; the Add tab's full height is only its ceiling.
						maxHeight={STAFF_PICKER.PANEL_HEIGHT}
					/>
				</ErrorBoundary>
			)}

			{activeTab === "create-shift" && (
				<ErrorBoundary>
					<CreateShiftContent
						role={inverseRolesMap[rolesToDisplay[0]]}
						shiftNo={shiftNo}
						setShiftNo={setShiftNo}
						weekIndex={weekIndex}
						dayNo={dayNo}
						isMasterSchedule={false}
						onClose={handleClose}
						onSelectedDateChange={onSelectedDateChange}
						defaultLocation={defaultRoom}
						defaultJobId={defaultJobId}
						defaultRoles={rolesToDisplay.length ? rolesToDisplay : undefined}
						defaultLockedTimes={lockedTimes}
						hideRecommendations={hideRecommendations ?? !!defaultRoom}
					/>
				</ErrorBoundary>
			)}

			{activeTab === "move-nurse" && (
				<ErrorBoundary>
					<MoveNurse weekIndex={weekIndex} shiftIndex={shiftNo} workingDayIndex={dayNo} onClose={handleClose} />
				</ErrorBoundary>
			)}

			{activeTab === "swap-staff" && (
				<ErrorBoundary>
					<SwapStaff
						weekIndex={weekIndex}
						shiftIndex={shiftNo}
						dayIndex={dayNo}
						onClose={handleClose}
						handleSwap={(originShiftId, targetShiftId) => swapNurses({ originShiftId, targetShiftId })}
					/>
				</ErrorBoundary>
			)}

			{activeTab === "recurring-shift" && (
				<ErrorBoundary>
					<RecurringShiftPanelWrapper
						renderWithoutModal={true}
						disableHidePopup={true}
						onClose={onClose}
						clickedDate={getShiftDate(weekIndex, shiftNo, dayNo)}
						shouldAddNewShiftOnOpen={false}
						isSynergyMS={false}
						initialRecurringShiftData={{
							...initialRecurringShiftDataStructure,
							selectedDays: [Object.values(DaysInWeek)[dayNo]],
						}}
					/>
				</ErrorBoundary>
			)}
		</Box>
	);
};

export const StaffingChanges = ({
	setIsAdd,
	dayNo,
	shiftNo: initialShiftNo,
	rolesToDisplay,
	onNurseSelected,
	hideRoomSelect = false,
	isCharged = false,
	onClose,
	onShiftChange,
	showActual = false,
	defaultTab,
	onSelectedDateChange,
	lockedTimes,
	defaultRoom,
	defaultJobId,
	hideRecommendations,
	initialShiftSelected,
	weekIndex: propWeekIndex,
}: StaffingChangesProps) => {
	const { t } = useTranslation();
	const { weekIndex: ctxWeekIndex } = useWeekIndex();
	const weekIndex = propWeekIndex ?? ctxWeekIndex;
	const { hospitalData, timezone, isSnf, isAutoScheduler } = useHospitalData();
	const [open, setOpen] = useState(true);
	const [shiftNo, setShiftNo] = useState(initialShiftNo);
	const [isMinimized, setIsMinimized] = useState(false);

	const isShiftInPast = useMemo(() => {
		const selectedShiftDate = hospitalData.weeks[weekIndex].weeklyShifts[shiftNo].days[dayNo].date;
		return isShiftDayInPast(selectedShiftDate, timezone);
	}, [hospitalData, weekIndex, shiftNo, dayNo, timezone]);

	// An explicitly requested tab wins. `isShiftInPast` used to be tested first, which threw the
	// request away on any past day: the day card's three icons each pass their own correct `defaultTab`
	// (create-shift / add-nurse / remove-nurse) and all three opened Remove Nurse, so "Add to Schedule"
	// landed the user on a destructive tab they hadn't asked for and cost a click to leave. Post Shift is
	// the one tab a past day genuinely can't serve -- it renders `disabled` in the strip below -- so that
	// request alone still falls through to the past-day default rather than opening a dead tab.
	//
	// It is refused outright on an auto-scheduler department, where the strip does not render the tab at
	// ALL (see `!isAutoScheduler` below). Honouring the request there mounted the Post Shift editor with
	// no tab selected in the strip above it -- an editor for something this department does not do, and
	// no way to see which tab you were on. Callers are expected to gate their own entry points, and the
	// monthly view does, but a shared panel should not depend on every caller remembering to.
	const canRequestCreateShift = !isShiftInPast && !isAutoScheduler;
	const requestedTab = defaultTab === "create-shift" && !canRequestCreateShift ? undefined : defaultTab;

	const [activeTab, setActiveTab] = useState<TabType>(
		requestedTab ?? (isShiftInPast ? "remove-nurse" : showActual ? "remove-nurse" : isSnf ? "create-shift" : isAutoScheduler ? "move-nurse" : "add-nurse"),
	);

	const handleClose = (success?: boolean) => {
		setOpen(false);
		onClose?.(success);
	};

	const maximizedBody = (
		<MaximizedStaffingChangesBody
			setIsAdd={setIsAdd}
			dayNo={dayNo}
			shiftNo={shiftNo}
			weekIndex={weekIndex}
			rolesToDisplay={rolesToDisplay}
			onNurseSelected={onNurseSelected}
			hideRoomSelect={hideRoomSelect}
			isCharged={isCharged}
			onClose={onClose}
			onShiftChange={onShiftChange}
			showActual={showActual}
			activeTab={activeTab}
			setActiveTab={setActiveTab}
			isShiftInPast={isShiftInPast}
			onSelectedDateChange={onSelectedDateChange}
			setOpen={setOpen}
			handleClose={handleClose}
			setShiftNo={setShiftNo}
			lockedTimes={lockedTimes}
			defaultRoom={defaultRoom}
			defaultJobId={defaultJobId}
			hideRecommendations={hideRecommendations}
			initialShiftSelected={initialShiftSelected}
		/>
	);

	const bodyRenderer = (currentSizeState: MultiSizePopupState) => {
		setIsMinimized(currentSizeState !== MultiSizePopupState.MAXIMIZED);

		const isMax = currentSizeState === MultiSizePopupState.MAXIMIZED;
		return (
			<Box
				sx={{
					visibility: isMax ? "visible" : "hidden",
					pointerEvents: isMax ? "auto" : "none",
					height: isMax ? "auto" : 0, // optional
					overflow: "hidden",
				}}
			>
				{maximizedBody}
			</Box>
		);
	};

	return (
		<MultiSizePopup
			onClose={(_) => handleClose(false)}
			data-testid={"staffing-changes-modal"}
			title={isMinimized ? t(tabToLabelKey[activeTab]) : t("staffing.changes.title")}
			open={open}
			disableMinimize={true}
			bodyRenderer={bodyRenderer}
			keepBodyMounted={true}
			closeOnEscape={true}
		/>
	);
};
